import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_END_POINT_NOTIFICATION, NOTIFICATION_END_POINT } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_END_POINT_NOTIFICATION}`
  }),
  tagTypes: ['Notification'],
  endpoints: (build) => ({
    // Get notifications by user ID
    findNotificationsByUserId: build.query({
      query: ({ userId }) => {
        const localAuth = getLocalStorageAuthentication();
        const userIdEncoded = encodeURIComponent(userId);
        const url = `${NOTIFICATION_END_POINT}/user/${userIdEncoded}`;
        
        console.log('🌐 [API] Fetching notifications from:', `${BASE_END_POINT_NOTIFICATION}${url}`);
        console.log('🔑 [API] Using JWT token:', localAuth.token ? 'Present' : 'Missing');
        
        return {
          url: url,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      providesTags: ['Notification'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [API] Successfully fetched', data?.length || 0, 'notifications');
        } catch (error) {
          const status = error?.error?.status;
          console.error('❌ [API] Failed to fetch notifications:', error);
          
          if (status === 404) {
            // No notifications found is not an error, just return empty array
            console.log('ℹ️ [API] No notifications found for user (404)');
          } else {
            dispatch(
              messageUpdated({
                text: 'Failed to fetch notifications',
                severity: Severity.ERROR
              })
            );
          }
        }
      }
    }),

    // Create a new notification
    createNotification: build.mutation({
      query: ({ topic, body, userId }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${NOTIFICATION_END_POINT}`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: {
            topic,
            body,
            user_id: userId
          }
        };
      },
      invalidatesTags: ['Notification'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled;
          dispatch(
            messageUpdated({
              text: 'Notification created successfully',
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          dispatch(
            messageUpdated({
              text: 'Failed to create notification',
              severity: Severity.ERROR
            })
          );
        }
      }
    }),

    // Update a notification
    updateNotification: build.mutation({
      query: ({ id, topic, body, read, userId }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${NOTIFICATION_END_POINT}`,
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: {
            id,
            topic,
            body,
            read,
            user_id: userId
          }
        };
      },
      invalidatesTags: ['Notification'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled;
          dispatch(
            messageUpdated({
              text: 'Notification updated successfully',
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          dispatch(
            messageUpdated({
              text: 'Failed to update notification',
              severity: Severity.ERROR
            })
          );
        }
      }
    }),

    // Mark notification as read
    markNotificationAsRead: build.mutation({
      query: ({ id }) => {
        const localAuth = getLocalStorageAuthentication();
        
        console.log('📖 [API] Marking notification as read:', id);
        
        return {
          url: `${NOTIFICATION_END_POINT}/${id}/read`,
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      invalidatesTags: ['Notification'],
      onQueryStarted: async (arg, { queryFulfilled }) => {
        try {
          await queryFulfilled;
          console.log('✅ [API] Notification marked as read:', arg.id);
        } catch (error) {
          console.error('❌ [API] Failed to mark notification as read:', error);
        }
      }
    }),

    // Delete a notification
    deleteNotification: build.mutation({
      query: ({ notificationId }) => {
        const localAuth = getLocalStorageAuthentication();
        const notificationIdEncoded = encodeURIComponent(notificationId);
        return {
          url: `${NOTIFICATION_END_POINT}/delete/${notificationIdEncoded}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      invalidatesTags: ['Notification'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled;
          dispatch(
            messageUpdated({
              text: 'Notification deleted successfully',
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          dispatch(
            messageUpdated({
              text: 'Failed to delete notification',
              severity: Severity.ERROR
            })
          );
        }
      }
    })
  })
});

// Export hooks for usage in functional components
export const {
  useFindNotificationsByUserIdQuery,
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useMarkNotificationAsReadMutation,
  useDeleteNotificationMutation
} = notificationApi;

